import { apiClient } from './apiClient';

export interface CampaignQaCsvViolation {
  line_number: number | null;
  field: string;
  code: string;
  severity: 'BLOCKER' | 'HIGH' | 'WARNING' | 'INFO' | string;
  message: string;
}

export interface CampaignQaCsvSummary {
  rows_total: number;
  row_type_counts: Record<string, number>;
  blockers: number;
  warnings: number;
  infos: number;
}

export interface CampaignQaRuleFinding {
  line_number: number | null;
  field: string | null;
  code: string;
  category: string;
  severity: 'BLOCKER' | 'HIGH' | 'WARNING' | 'INFO' | string;
  message: string;
  expected: string | null;
  actual: string | null;
}

export interface CampaignQaRuleSummary {
  verdict: 'PASS' | 'PASS_WITH_WARNINGS' | 'BLOCKED' | string;
  blockers: number;
  warnings: number;
  infos: number;
}

export interface CampaignQaCsvValidateResult {
  tenant_id: string;
  is_valid: boolean;
  summary: CampaignQaCsvSummary;
  violations: CampaignQaCsvViolation[];
  rule_summary: CampaignQaRuleSummary;
  rule_findings: CampaignQaRuleFinding[];
}

export interface CampaignQaRunDiffFinding {
  line_number: number | null;
  field: string | null;
  code: string;
  category: string;
  severity: 'BLOCKER' | 'HIGH' | 'WARNING' | 'INFO' | string;
  message: string;
  expected: string | null;
  actual: string | null;
}

export interface CampaignQaRunDiffSummary {
  verdict: 'PASS' | 'PASS_WITH_WARNINGS' | 'BLOCKED' | string;
  blockers: number;
  warnings: number;
  infos: number;
  compared_campaign_names: string[];
  readback_checked_at: string | null;
}

export interface CampaignQaRunDiffResult {
  tenant_id: string;
  run_id: string;
  campaign_resource_name: string | null;
  summary: CampaignQaRunDiffSummary;
  findings: CampaignQaRunDiffFinding[];
}

export interface CampaignQaRunGenerateResult {
  tenant_id: string;
  run_id: string;
  file_name: string;
  csv_text: string;
  campaign_names: string[];
  rows_total: number;
  source: string;
}

export interface CampaignQaGoogleAdsExportGenerateResult {
  tenant_id: string;
  file_name: string;
  csv_text: string;
  campaign_names: string[];
  rows_total: number;
  source: string;
}

export interface CampaignQaTemplateDownload {
  blob: Blob;
  filename: string;
}

function filenameFromContentDisposition(
  contentDisposition: string | undefined,
  fallback: string,
): string {
  const match = contentDisposition?.match(/filename="?([^";]+)"?/i);
  return match?.[1] || fallback;
}

export async function validateCampaignQaCsv(params: {
  tenantId: string;
  csvText: string;
}): Promise<CampaignQaCsvValidateResult> {
  const { data } = await apiClient.post<CampaignQaCsvValidateResult>(
    '/google-ads/campaign-qa/validate-csv',
    { csv_text: params.csvText },
    { params: { tenant_id: params.tenantId } },
  );
  return data;
}

export async function generateCampaignQaCsvFromRun(params: {
  tenantId: string;
  runId: string;
  campaignName?: string | null;
}): Promise<CampaignQaRunGenerateResult> {
  const { data } = await apiClient.post<CampaignQaRunGenerateResult>(
    '/google-ads/campaign-qa/generate-from-run',
    {
      run_id: params.runId,
      campaign_name: params.campaignName || null,
    },
    { params: { tenant_id: params.tenantId } },
  );
  return data;
}

export async function generateCampaignQaCsvFromGoogleAdsExport(params: {
  tenantId: string;
  csvText: string;
  fileName?: string | null;
}): Promise<CampaignQaGoogleAdsExportGenerateResult> {
  const { data } = await apiClient.post<CampaignQaGoogleAdsExportGenerateResult>(
    '/google-ads/campaign-qa/generate-from-google-ads-export',
    {
      csv_text: params.csvText,
      file_name: params.fileName || null,
    },
    { params: { tenant_id: params.tenantId } },
  );
  return data;
}

export async function diffCampaignQaRun(params: {
  tenantId: string;
  csvText: string;
  runId: string;
  campaignName?: string | null;
}): Promise<CampaignQaRunDiffResult> {
  const { data } = await apiClient.post<CampaignQaRunDiffResult>(
    '/google-ads/campaign-qa/diff-run',
    {
      csv_text: params.csvText,
      run_id: params.runId,
      campaign_name: params.campaignName || null,
    },
    { params: { tenant_id: params.tenantId } },
  );
  return data;
}

export async function downloadCampaignQaTemplate(
  tenantId: string,
): Promise<CampaignQaTemplateDownload> {
  const response = await apiClient.get<Blob>(
    '/google-ads/campaign-qa/template.csv',
    {
      params: { tenant_id: tenantId },
      responseType: 'blob',
    },
  );
  return {
    blob: response.data,
    filename: filenameFromContentDisposition(
      response.headers['content-disposition'],
      'campaign-qa-csv-template.csv',
    ),
  };
}
